import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { FaRegTrashAlt, FaShoppingCart, FaPlus, FaMinus } from 'react-icons/fa';
import { BodyText, Subtitle } from './ui';

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const TableWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  margin-bottom: 5vh;
  box-sizing: border-box;

  @media (max-width: 768px) {
    margin-bottom: 3vh;
  }

  @media (max-width: 480px) {
    margin-bottom: 2vh;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${(props) => props.theme.colors.white};
  table-layout: fixed;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableHeadContainer = styled.thead`
  background: ${(props) => props.theme.colors.subBackground};
  width: 100%;
`;

const TableHeadItem = styled.th`
  padding: 10px 6px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:nth-child(1) {
    width: 7%;
  }
  &:nth-child(2) {
    width: 8%;
  }
  &:nth-child(3) {
    width: 11%;
  }
  &:nth-child(4) {
    width: 22%;
  }
  &:nth-child(5) {
    width: 10%;
  }
  &:nth-child(6) {
    width: 11%;
  }
  &:nth-child(7) {
    width: 8%;
  }
  &:nth-child(8) {
    width: 8%;
  }
  &:nth-child(9) {
    width: 10%;
  }
  &:nth-child(10) {
    width: 5%;
  }

  @media (max-width: 768px) {
    padding: 8px 4px;
  }
`;

const TableItem = styled.tbody`
  width: 100%;
`;

const TableItemContainer = styled.tr`
  border-bottom: 1px solid #ebebeb;
  width: 100%;
`;

const TableData = styled.td`
  padding: 10px 6px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.primaryText};
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;

  &:nth-child(4) {
    white-space: normal;
    word-wrap: break-word;
  }

  @media (max-width: 768px) {
    padding: 8px 4px;
    font-size: 11px;
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  border-radius: 5px;
  background: ${(props) => props.theme.colors.subBackground};
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 110px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    max-width: 100px;
  }

  @media (max-width: 480px) {
    max-width: 80px;
  }
`;

const QuantityButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px;
  background: ${(props) => props.theme.colors.btnSubBackground};
  border: none;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background: #ccc;
  }

  @media (max-width: 768px) {
    padding: 6px;
  }

  @media (max-width: 480px) {
    padding: 4px;
  }
`;

const QuantityInput = styled.input`
  width: 34px;
  min-width: 0;
  margin: 0 3px;
  padding: 4px 1px;
  font: inherit;
  font-weight: 600;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 3px;
  background: ${(props) => props.theme.colors.white};

  @media (max-width: 768px) {
    width: 30px;
    margin: 0 2px;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    width: 26px;
    margin: 0 1px;
    font-size: 0.8rem;
  }
`;

const QuantityDisplay = styled.span`
  margin: 0 8px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
`;

const Button = styled.button`
  background-color: ${(props) => props.theme.colors.primaryBlue};
  color: white;
  padding: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  min-width: 36px;
  height: 36px;
  border-radius: 8px;
  box-sizing: border-box;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    padding: 6px;
    min-width: 32px;
    height: 32px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    padding: 5px;
    min-width: 28px;
    height: 28px;
    border-radius: 5px;
  }
`;

// Mobile Card Layout
const MobileCardContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const MobileCard = styled.div`
  background: ${(props) => props.theme.colors.white};
  border: 1px solid #ebebeb;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 10px;
    gap: 8px;
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ebebeb;
  padding-bottom: 10px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding-bottom: 8px;
  }
`;

const MobileCardContent = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const MobileCardImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
  }
`;

const MobileCardDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  overflow: hidden;
`;

const MobileCardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #ebebeb;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    margin-top: 8px;
    padding-top: 8px;
  }
`;

export default function ItemTable({
  style,
  data,
  updateData,
  removeData,
  editable = true,
  showAddCartBtn,
  showDeleteBtn = true,
  allowQuantityInput = false,
}) {
  const theme = useTheme();
  const [quantityInputs, setQuantityInputs] = useState({});
  const formatSrpPrice = (srp) => {
    if (srp === null || srp === undefined || srp === '') return '-';
    const numericSrp = parseFloat(String(srp).replace(/[$,]/g, '').trim());
    if (Number.isNaN(numericSrp)) return '-';
    return `$${numericSrp.toFixed(2)}`;
  };

  // Calculate total quantity of all items with the same idStyle in cart
  const getTotalQuantityForProduct = (idStyle) => {
    return data
      .filter((cartItem) => cartItem.idStyle === idStyle)
      .reduce((sum, cartItem) => sum + (cartItem.totQty || 0), 0);
  };

  const handleQuantity = (item, quantity) => {
    const numQuantity = parseFloat(quantity);
    if (numQuantity > 0) {
      setQuantityInputs((current) => ({
        ...current,
        [item.idCartDtl || `${item.idStyle}-${item.idStyleDtl}`]: numQuantity,
      }));
      updateData(item, numQuantity);
    }
  };

  const getItemKey = (item) =>
    item.idCartDtl || `${item.idStyle}-${item.idStyleDtl}`;

  const getInputValue = (item) => {
    const itemKey = getItemKey(item);
    return quantityInputs[itemKey] ?? item.totQty;
  };

  const handleQuantityInputChange = (item, value) => {
    if (/^\d*$/.test(value)) {
      setQuantityInputs((current) => ({
        ...current,
        [getItemKey(item)]: value,
      }));
    }
  };

  const commitQuantityInput = (item) => {
    const itemKey = getItemKey(item);
    const requestedQuantity = Number(quantityInputs[itemKey] ?? item.totQty);
    const packQuantity = Number(item.qtyPerUOM) || 1;
    let adjustedQuantity =
      Math.floor(requestedQuantity / packQuantity) * packQuantity;

    if (item.avaQty !== undefined && item.avaQty !== null) {
      const otherItemsQuantity =
        getTotalQuantityForProduct(item.idStyle) - item.totQty;
      const maxAllowedQuantity = Math.max(0, item.avaQty - otherItemsQuantity);
      adjustedQuantity = Math.min(
        adjustedQuantity,
        Math.floor(maxAllowedQuantity / packQuantity) * packQuantity,
      );
    }

    // A cart line cannot be updated to zero; restore the current value instead.
    if (adjustedQuantity <= 0) {
      setQuantityInputs((current) => ({ ...current, [itemKey]: item.totQty }));
      return;
    }

    setQuantityInputs((current) => ({
      ...current,
      [itemKey]: adjustedQuantity,
    }));
    if (adjustedQuantity !== item.totQty) {
      handleQuantity(item, adjustedQuantity);
    }
  };

  const renderQuantityValue = (item) =>
    allowQuantityInput ? (
      <QuantityInput
        aria-label={`Quantity for ${item.webTitle}`}
        inputMode='numeric'
        min={item.qtyPerUOM || 1}
        step={item.qtyPerUOM || 1}
        type='text'
        value={getInputValue(item)}
        onChange={(event) =>
          handleQuantityInputChange(item, event.target.value)
        }
        onBlur={() => commitQuantityInput(item)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          }
        }}
      />
    ) : (
      <QuantityDisplay>{item.totQty}</QuantityDisplay>
    );

  const handleRemove = (item) => {
    removeData(item);
  };

  const renderItem = (item) => {
    return (
      <TableItem>
        <TableItemContainer>
          <TableData>
            <img
              src={`${process.env.REACT_APP_FILE_UPLOAD_URL}${item.filepath1}`}
              alt='Product'
              style={{
                display: 'block',
                width: '100%',
                height: 'auto',
                maxHeight: '80px',
                objectFit: 'cover',
              }}
            />
          </TableData>
          <TableData>
            <strong style={{ fontSize: '0.75rem' }}>{item.idStyle}</strong>
          </TableData>
          <TableData>
            <BodyText size='0.75rem'>{item.barcode || '-'}</BodyText>
          </TableData>
          <TableData>
            <BodyText size='0.75rem'>{item.webTitle}</BodyText>
          </TableData>
          <TableData>
            <BodyText size='0.75rem'>
              {item.qtyPerUOM && item.qtyPerCTN
                ? `${item.qtyPerUOM}/${item.qtyPerCTN} (Pack/CTN)`
                : '-'}
            </BodyText>
          </TableData>
          <TableData>
            {editable ? (
              <QuantitySelector>
                <QuantityButton
                  disabled={item.totQty - (item.qtyPerUOM || 1) <= 0}
                  onClick={() => {
                    const packQuantity = item.qtyPerUOM || 1;
                    const newQuantity = item.totQty - packQuantity;
                    if (newQuantity > 0) {
                      handleQuantity(item, newQuantity);
                    }
                  }}
                >
                  <FaMinus />
                </QuantityButton>
                {renderQuantityValue(item)}
                <QuantityButton
                  disabled={
                    item.avaQty !== undefined &&
                    item.avaQty !== null &&
                    (() => {
                      const packQuantity = item.qtyPerUOM || 1;
                      const newQuantity = item.totQty + packQuantity;
                      const currentTotalForProduct = getTotalQuantityForProduct(
                        item.idStyle,
                        item.idStyleDtl,
                      );
                      const newTotalForProduct =
                        currentTotalForProduct - item.totQty + newQuantity;
                      return newTotalForProduct > item.avaQty;
                    })()
                  }
                  onClick={() => {
                    const packQuantity = item.qtyPerUOM || 1;
                    const newQuantity = item.totQty + packQuantity;

                    // Check if this would exceed available quantity
                    if (item.avaQty !== undefined && item.avaQty !== null) {
                      const currentTotalForProduct = getTotalQuantityForProduct(
                        item.idStyle,
                        item.idStyleDtl,
                      );
                      const newTotalForProduct =
                        currentTotalForProduct - item.totQty + newQuantity;

                      if (newTotalForProduct > item.avaQty) {
                        alert(
                          `Cannot exceed available quantity of ${item.avaQty}. Current total in cart: ${currentTotalForProduct}`,
                        );
                        return;
                      }
                    }

                    handleQuantity(item, newQuantity);
                  }}
                >
                  <FaPlus />
                </QuantityButton>
              </QuantitySelector>
            ) : (
              <BodyText size='0.75rem'>{item.totQty}</BodyText>
            )}
          </TableData>
          <TableData>
            <BodyText size='0.75rem'>{`$${item.price.toFixed(2)}`}</BodyText>
          </TableData>
          <TableData>
            <strong
              style={{ fontSize: '0.75rem' }}
            >{`$${(item.totQty * item.price).toFixed(2)}`}</strong>
          </TableData>
          <TableData>
            <BodyText size='0.75rem'>{formatSrpPrice(item.srp)}</BodyText>
          </TableData>
          <TableData>
            <Column style={{ gap: '5px', alignItems: 'center' }}>
              {showAddCartBtn ? (
                <Button>
                  <FaShoppingCart />
                </Button>
              ) : null}
              {showDeleteBtn ? (
                <Button
                  variant='destructive'
                  style={{ background: '#474747', color: '#ffffff' }}
                  onClick={() => {
                    handleRemove(item);
                  }}
                >
                  <FaRegTrashAlt />
                </Button>
              ) : null}
            </Column>
          </TableData>
        </TableItemContainer>
      </TableItem>
    );
  };

  const renderMobileItem = (item, idx) => {
    return (
      <MobileCard key={idx}>
        <MobileCardHeader>
          <Subtitle>{item.idStyle}</Subtitle>
          <BodyText style={{ color: theme.colors.primaryRed }}>
            ${(item.totQty * item.price).toFixed(2)}
          </BodyText>
        </MobileCardHeader>

        <MobileCardContent>
          <MobileCardImage
            src={`${process.env.REACT_APP_FILE_UPLOAD_URL}${item.filepath1}`}
            alt='Product'
          />
          <MobileCardDetails>
            <BodyText style={{ fontWeight: 'bold' }}>{item.webTitle}</BodyText>
            <BodyText>SRP: {formatSrpPrice(item.srp)}</BodyText>
            <BodyText>
              UoM:{' '}
              {item.qtyPerUOM && item.qtyPerCTN
                ? `${item.qtyPerUOM}/${item.qtyPerCTN} (Pack/CTN)`
                : '-'}
            </BodyText>
            <BodyText>Barcode: {item.barcode || '-'}</BodyText>
            <BodyText>Price: ${item.price.toFixed(2)}</BodyText>
            <BodyText>Quantity: {item.totQty}</BodyText>
          </MobileCardDetails>
        </MobileCardContent>

        <MobileCardActions>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BodyText>Qty:</BodyText>
            {editable ? (
              <QuantitySelector>
                <QuantityButton
                  disabled={item.totQty - (item.qtyPerUOM || 1) <= 0}
                  onClick={() => {
                    const packQuantity = item.qtyPerUOM || 1;
                    const newQuantity = item.totQty - packQuantity;
                    if (newQuantity > 0) {
                      handleQuantity(item, newQuantity);
                    }
                  }}
                >
                  <FaMinus />
                </QuantityButton>
                {renderQuantityValue(item)}
                <QuantityButton
                  disabled={
                    item.avaQty !== undefined &&
                    item.avaQty !== null &&
                    (() => {
                      const packQuantity = item.qtyPerUOM || 1;
                      const newQuantity = item.totQty + packQuantity;
                      const currentTotalForProduct = getTotalQuantityForProduct(
                        item.idStyle,
                        item.idStyleDtl,
                      );
                      const newTotalForProduct =
                        currentTotalForProduct - item.totQty + newQuantity;
                      return newTotalForProduct > item.avaQty;
                    })()
                  }
                  onClick={() => {
                    const packQuantity = item.qtyPerUOM || 1;
                    const newQuantity = item.totQty + packQuantity;

                    // Check if this would exceed available quantity
                    if (item.avaQty !== undefined && item.avaQty !== null) {
                      const currentTotalForProduct = getTotalQuantityForProduct(
                        item.idStyle,
                        item.idStyleDtl,
                      );
                      const newTotalForProduct =
                        currentTotalForProduct - item.totQty + newQuantity;

                      if (newTotalForProduct > item.avaQty) {
                        alert(
                          `Cannot exceed available quantity of ${item.avaQty}. Current total in cart: ${currentTotalForProduct}`,
                        );
                        return;
                      }
                    }

                    handleQuantity(item, newQuantity);
                  }}
                >
                  <FaPlus />
                </QuantityButton>
              </QuantitySelector>
            ) : (
              <BodyText>{item.totQty}</BodyText>
            )}
          </div>

          <div style={{ display: 'flex', gap: '5px' }}>
            {showAddCartBtn && (
              <Button>
                <FaShoppingCart />
              </Button>
            )}
            {showDeleteBtn && (
              <Button
                variant='destructive'
                style={{ background: '#474747', color: '#ffffff' }}
                onClick={() => handleRemove(item)}
              >
                <FaRegTrashAlt />
              </Button>
            )}
          </div>
        </MobileCardActions>
      </MobileCard>
    );
  };

  if (!data || data.length === 0) {
    return (
      <TableWrapper style={style}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px',
            color: theme.colors.primaryText,
            fontSize: '16px',
          }}
        >
          No items found
        </div>
      </TableWrapper>
    );
  }

  return (
    <TableWrapper style={style}>
      <Table>
        <TableHeadContainer>
          <tr>
            <TableHeadItem>
              <Subtitle size='0.75rem'>Image</Subtitle>
            </TableHeadItem>
            <TableHeadItem>
              <Subtitle size='0.75rem'>SKU</Subtitle>
            </TableHeadItem>
            <TableHeadItem>
              <Subtitle size='0.75rem'>Barcode</Subtitle>
            </TableHeadItem>
            <TableHeadItem>
              <Subtitle size='0.75rem'>Product(s)</Subtitle>
            </TableHeadItem>
            <TableHeadItem>
              <Subtitle size='0.75rem'>UoM</Subtitle>
            </TableHeadItem>
            <TableHeadItem>
              <Subtitle size='0.75rem'>Qty</Subtitle>
            </TableHeadItem>
            <TableHeadItem>
              <Subtitle size='0.75rem'>Price</Subtitle>
            </TableHeadItem>
            <TableHeadItem>
              <Subtitle size='0.75rem'>Total</Subtitle>
            </TableHeadItem>
            <TableHeadItem>
              <Subtitle size='0.75rem'>SRP</Subtitle>
            </TableHeadItem>
            <TableHeadItem></TableHeadItem>
          </tr>
        </TableHeadContainer>
        {data.map((item) => {
          return renderItem(item);
        })}
      </Table>

      <MobileCardContainer>
        {data.map((item, idx) => {
          return renderMobileItem(item, idx);
        })}
      </MobileCardContainer>
    </TableWrapper>
  );
}
